import { camelCase, isNaN } from "lodash-es";

export interface Breadcrumb {
  href: string;
  label: string;
  current?: boolean;
}

export type GenerateBreadcrumbsInput = {
  labelGenerator: (path: string) => string;
  title?: string;
  asPath: string;
};

export type GenerateBreadcrumbsOutput = Breadcrumb[];

export const generateBreadcrumbs = ({
  labelGenerator,
  title,
  asPath,
}: GenerateBreadcrumbsInput): GenerateBreadcrumbsOutput => {
  const pathWithoutQuery = asPath.split("?")[0];

  let pathArray = pathWithoutQuery.split("/");

  pathArray.shift();

  pathArray = pathArray.filter((path) => path !== "");

  const isNanPathArray = pathArray.filter((path) => isNaN(+path));

  if (isNanPathArray.length <= 1) {
    return [];
  }

  const breadcrumbs = isNanPathArray.map((path, index) => {
    const href = "/" + pathArray.slice(0, index + 1).join("/");

    return {
      href,
      label: labelGenerator(camelCase(path)) ?? title,
      current: index === isNanPathArray.length - 1,
    };
  });

  const lastSegment = pathArray[pathArray.length - 1];
  const lastStaticSegment = isNanPathArray[isNanPathArray.length - 1];

  /**
   * A trailing id segment (`/consignment/customer-stocks/12`) is filtered out
   * above, so a detail page can only get a crumb of its own from a label
   * registered explicitly under `<parentSegment>Detail` — e.g.
   * `customerStocksDetail`. Routes without such a label keep their existing
   * output, so this never adds an unlabelled crumb to the detail pages that
   * rely on a trailing static `/detail` segment instead.
   */
  const detailLabel = isNaN(+lastSegment)
    ? undefined
    : labelGenerator(camelCase(lastStaticSegment) + "Detail");

  return detailLabel
    ? [
        ...breadcrumbs.map((breadcrumb) => ({
          ...breadcrumb,
          current: false,
        })),
        {
          href: pathWithoutQuery,
          label: detailLabel,
          current: true,
        },
      ]
    : breadcrumbs;
};

export default generateBreadcrumbs;
